import { useEffect, useRef, useState } from "react";
import { NarrationSegment } from "../services/narration";

interface UseSufiVoiceOptions {
  /** Everything to read for the current section, in order. A new array restarts narration. */
  segments: NarrationSegment[];
  /** Voice ON / OFF toggle */
  voiceEnabled: boolean;
  /** Play / Pause toggle */
  playing: boolean;
  /** Called once the last segment has been read */
  onDone?: () => void;
}

/**
 * Helper to select the highest-quality female voice available in the browser.
 */
function getFemaleVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (!voices || voices.length === 0) return null;

  const femaleNames = [
    "Microsoft Zira",
    "Zira",
    "Microsoft Jenny Online",
    "Microsoft Jenny",
    "Microsoft Aria Online",
    "Microsoft Aria",
    "Google UK English Female",
    "Google US English Female",
    "Samantha",
    "Victoria",
    "Karen",
    "Fiona",
    "Moira",
    "Veena",
  ];

  for (const name of femaleNames) {
    const match = voices.find((v) => v.name.toLowerCase().includes(name.toLowerCase()));
    if (match) return match;
  }

  const genericFemale = voices.find((v) => v.name.toLowerCase().includes("female"));
  if (genericFemale) return genericFemale;

  const enFemaleCandidate = voices.find(
    (v) =>
      v.lang.startsWith("en") &&
      !v.name.toLowerCase().includes("david") &&
      !v.name.toLowerCase().includes("mark") &&
      !v.name.toLowerCase().includes("george") &&
      !v.name.toLowerCase().includes("male")
  );
  if (enFemaleCandidate) return enFemaleCandidate;

  return voices[0] || null;
}

/**
 * Reads each segment in order with speech synthesis (or silent simulation if muted)
 * so that Sufi always reads the full section, highlighting each element and signaling completion.
 */
export function useSufiVoice({
  segments,
  voiceEnabled,
  playing,
  onDone,
}: UseSufiVoiceOptions) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeCharIndex, setActiveCharIndex] = useState<number | null>(null);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const startedRef = useRef(false);

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // Load voices and listen for voiceschanged
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;

    const updateVoices = () => {
      const v = synth.getVoices();
      if (v && v.length > 0) {
        setAvailableVoices(v);
      }
    };

    updateVoices();
    synth.onvoiceschanged = updateVoices;

    return () => {
      synth.onvoiceschanged = null;
    };
  }, [supported]);

  // Main narration effect
  useEffect(() => {
    setIsCompleted(false);
    setActiveId(null);
    startedRef.current = false;

    if (segments.length === 0) {
      setIsCompleted(true);
      onDoneRef.current?.();
      return;
    }

    if (!supported) {
      // Fallback timer when speech synthesis is not supported
      let currentIndex = 0;
      let timer: any = null;

      const step = () => {
        if (!playingRef.current) return;
        if (currentIndex < segments.length) {
          setActiveId(segments[currentIndex].id);
          const words = segments[currentIndex].text.split(" ").length;
          const duration = Math.max(1500, (words / 2.8) * 1000);
          currentIndex++;
          timer = setTimeout(step, duration);
        } else {
          setActiveId(null);
          setIsCompleted(true);
          onDoneRef.current?.();
        }
      };

      step();
      return () => clearTimeout(timer);
    }

    const synth = window.speechSynthesis;
    synth.cancel();
    if (playingRef.current) synth.resume();

    const voices = availableVoices.length > 0 ? availableVoices : synth.getVoices();
    const femaleVoice = getFemaleVoice(voices);

    let cancelled = false;
    let charTickerTimer: any = null;

    const stopCharTicker = () => {
      if (charTickerTimer) {
        clearInterval(charTickerTimer);
        charTickerTimer = null;
      }
    };

    const advanceWordByAlphabet = (wordStart: number, fullText: string) => {
      stopCharTicker();
      if (cancelled || !playingRef.current) return;

      const remaining = fullText.slice(wordStart);
      const match = remaining.match(/^[^\s]+/);
      const wordLen = match ? match[0].length : 1;

      let currentOffset = 0;
      setActiveCharIndex(wordStart);

      // Smoothly advance character by character (~45-60ms per alphabet)
      const intervalMs = Math.max(38, Math.min(65, 360 / wordLen));
      charTickerTimer = setInterval(() => {
        if (cancelled || !playingRef.current) {
          stopCharTicker();
          return;
        }
        currentOffset++;
        if (currentOffset < wordLen) {
          setActiveCharIndex(wordStart + currentOffset);
        } else {
          stopCharTicker();
        }
      }, intervalMs);
    };

    segments.forEach((segment, i) => {
      const utterance = new SpeechSynthesisUtterance(segment.text);
      utterance.rate = 0.95;
      utterance.pitch = 1.05;
      // If voice is muted (off), set volume to 0 so it still reads silently and fires callbacks
      utterance.volume = voiceEnabled ? 1 : 0;

      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }

      utterance.onstart = () => {
        if (!cancelled) {
          setActiveId(segment.id);
          advanceWordByAlphabet(0, segment.text);
        }
      };

      utterance.onboundary = (e: SpeechSynthesisEvent) => {
        if (!cancelled && e.name === "word") {
          advanceWordByAlphabet(e.charIndex, segment.text);
        }
      };

      utterance.onend = () => {
        if (cancelled) return;
        stopCharTicker();
        setActiveCharIndex(null);
        if (i === segments.length - 1) {
          setActiveId(null);
          setIsCompleted(true);
          onDoneRef.current?.();
        }
      };

      utterance.onerror = () => {
        if (cancelled) return;
        stopCharTicker();
        setActiveCharIndex(null);
        if (i === segments.length - 1) {
          setActiveId(null);
          setIsCompleted(true);
          onDoneRef.current?.();
        }
      };

      synth.speak(utterance);
    });

    if (!playingRef.current) synth.pause();
    startedRef.current = true;

    return () => {
      cancelled = true;
      stopCharTicker();
      synth.cancel();
    };
  }, [segments, voiceEnabled, supported, availableVoices]);

  // Pause and resume
  useEffect(() => {
    if (!supported || !startedRef.current) return;
    const synth = window.speechSynthesis;
    if (playing) {
      synth.resume();
    } else {
      synth.pause();
    }
  }, [playing, supported]);

  return {
    supported,
    /** The segment currently being read */
    activeId: playing ? activeId : null,
    /** Real-time character index of the word currently spoken */
    activeCharIndex: playing ? activeCharIndex : null,
    /** True while reading is taking place */
    speaking: activeId !== null && playing,
    /** True once the entire section has been completely read */
    isCompleted,
  };
}